/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

import { useRequest } from 'ahooks';
import {
  resetUserStore,
  setUserInfo,
  useLoginStatus,
  persistSession,
  decodeJwtClaims,
  getAccessToken,
  getActiveTenantId,
  clearSession,
  type UserInfo,
} from '@trebox-foundation/account-adapter';

// Prefix absolute /api paths with the configured API base (TREBOX_API_BASE_URL ·
// build-time) so login-flow fetches hit the dedicated API host. Empty → same-origin.
const API_BASE_URL = process.env.TREBOX_API_BASE_URL || '';
const apiBase = (path: string): string =>
  API_BASE_URL && path.startsWith('/') ? API_BASE_URL + path : path;

// trebox `/api/auth/login` is the unified login entrypoint. It accepts a
// `grant_type` discriminator (password | identifier | otp) and dispatches
// internally. Identifier can be email/phone/username; the backend
// IdentifyType heuristic resolves the kind. The legacy `email` field is
// still honoured server-side as a fallback (LoginRequest.EffectiveIdentifier).
//
// Response shape:
//
//   { action: "token", token: { access_token, refresh_token, ... } }
// or
//   { action: "select_tenant", selection_token, tenants: [...] }
//
// rsbuild dev proxy routes /api/* to treboxd (see rsbuild.config.ts).
interface LoginToken {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in: number;
  principal_id: string;
  principal_kind: string;
  tenant_id: string;
}

interface LoginResponse {
  action: 'token' | 'select_tenant';
  token?: LoginToken;
  selection_token?: string;
  tenants?: Array<{ id: string; name: string; slug: string }>;
}

interface ErrorBody {
  code?: string;
  message?: string;
  error?: { reason?: string; message?: string };
}

export type GrantType = 'password' | 'identifier' | 'otp';

interface LoginParams {
  grantType?: GrantType;
  identifier: string;
  password?: string;
  code?: string;
  channel?: string;
}

async function postLogin(params: LoginParams): Promise<LoginToken> {
  const body = {
    grant_type: params.grantType ?? 'password',
    identifier: params.identifier,
    password: params.password,
    code: params.code,
    channel: params.channel,
  };
  const res = await fetch(apiBase('/api/auth/login'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as ErrorBody;
    const msg =
      err.error?.message ||
      err.message ||
      err.error?.reason ||
      `login failed (${res.status})`;
    throw new Error(msg);
  }
  const respBody = (await res.json()) as LoginResponse;
  if (respBody.action === 'select_tenant') {
    // Multi-tenant operator (e.g. the bootstrap admin joined to the brand
    // tenants where devices live): auto-select a tenant to get into the app,
    // preferring the last-used one so the choice is sticky across logins. The
    // left-rail tenant switcher is the primary surface for changing tenant
    // afterwards — see components/left-rail/workspace-switcher.
    const tenants = respBody.tenants ?? [];
    if (!respBody.selection_token || tenants.length === 0) {
      throw new Error('multi-tenant login returned no selectable tenants');
    }
    const preferred = getActiveTenantId();
    const chosen = tenants.find(t => t.id === preferred) ?? tenants[0];
    return selectTenant(respBody.selection_token, chosen.id);
  }
  if (!respBody.token) {
    throw new Error('login response missing token');
  }
  return respBody.token;
}

// selectTenant exchanges a multi-tenant login's selection_token + chosen
// tenant for the real JWT pair. The response is a bare token DTO, so tenant_id
// / principal_id are recovered from the JWT claims.
async function selectTenant(
  selectionToken: string,
  tenantId: string,
): Promise<LoginToken> {
  const res = await fetch(apiBase('/api/auth/select-tenant'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      selection_token: selectionToken,
      tenant_id: tenantId,
    }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as ErrorBody;
    throw new Error(
      err.error?.message ||
        err.message ||
        `tenant selection failed (${res.status})`,
    );
  }
  const body = (await res.json()) as Partial<LoginToken>;
  const claims = decodeJwtClaims(body.access_token ?? '');
  return {
    access_token: body.access_token ?? '',
    refresh_token: body.refresh_token ?? '',
    expires_in: body.expires_in ?? 0,
    refresh_expires_in: body.refresh_expires_in ?? 0,
    principal_id: claims.sub ?? '',
    principal_kind: 'user',
    tenant_id: tenantId || claims.tid || '',
  };
}

// persistToken adapts the login token DTO onto the shared session writer
// (single SoT · account-adapter/trebox-session). tenant_id / principal_id are
// taken from the DTO when present, else recovered from the JWT.
function persistToken(token: LoginToken) {
  persistSession(token.access_token, token.refresh_token, token.tenant_id);
}

// trebox-native logout. Server-side session invalidation is best-effort —
// even if `/api/auth/logout` fails (network · 401 from already-expired
// token · backend unavailable) the local session is cleared so the user
// gets out and lands on /sign.
export async function logoutTrebox(): Promise<void> {
  const token = getAccessToken();
  if (token) {
    try {
      await fetch(apiBase('/api/auth/logout'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (err) {
      // Server unreachable — log but proceed with local cleanup so the
      // user always lands on /sign even if the backend session can't be
      // revoked remotely.

      console.warn('[logout] server logout failed, clearing locally', err);
    }
  }
  clearSession();
  resetUserStore();
}

function tokenToUserInfo(identifier: string, token: LoginToken): UserInfo {
  // useLoginStatus only inspects user_id_str + isSettled; everything else
  // is a placeholder to keep the legacy UserInfo shape happy.
  return {
    user_id_str: token.principal_id,
    user_id: 0,
    name: identifier,
    screen_name: identifier,
    email: identifier.includes('@') ? identifier : '',
    email_collected: identifier.includes('@'),
  } as unknown as UserInfo;
}

// OIDC provider names recognized by the backend. The frontend forwards the
// user to /api/auth/oidc/authorize?provider_id=… ; provider_id is sourced
// from the discovery endpoint below.
export interface OIDCProvider {
  id: number;
  name: string;
  display_name: string;
}

export async function listOIDCProviders(): Promise<OIDCProvider[]> {
  try {
    const res = await fetch(apiBase('/api/auth/oidc/providers'), {
      method: 'GET',
    });
    if (!res.ok) {
      return [];
    }
    const body = (await res.json()) as { providers?: OIDCProvider[] };
    return body.providers ?? [];
  } catch (err) {
    // Backend doesn't ship a public providers list yet (M1) · empty array
    // hides the OIDC buttons.
    void err;
    return [];
  }
}

// startOIDCFlow redirects the browser to /api/auth/oidc/authorize, which the
// backend turns into a 302 to the upstream IdP. The post-callback handler
// (pages/oauth-callback/) consumes the token JSON.
export function startOIDCFlow(providerId: number, redirectUri: string) {
  const url = new URL('/api/auth/oidc/authorize', window.location.origin);
  url.searchParams.set('provider_id', String(providerId));
  url.searchParams.set('redirect_uri', redirectUri);
  window.location.assign(url.toString());
}

export const useLoginService = ({
  email,
  password,
}: {
  email: string;
  password: string;
}) => {
  const navigate = useNavigate();
  const loginStatus = useLoginStatus();

  const loginService = useRequest(
    async () => {
      const token = await postLogin({
        grantType: 'password',
        identifier: email,
        password,
      });
      persistToken(token);
      return tokenToUserInfo(email, token);
    },
    {
      manual: true,
      onSuccess: userInfo => {
        // Hydrate the global user store so downstream pages see the new
        // session immediately.
        setUserInfo(userInfo);
        // FULL-PAGE load (not SPA navigate) so the app re-bootstraps with the
        // new session: React Query caches populated WHILE logged out are dropped
        // and refetched with the token. Without this, an in-session login (e.g.
        // first login on a fresh origin) leaves the left-rail ['auth','tenants']
        // query stuck on its pre-login 401→[] result for staleTime, so the
        // workspace switcher stays hidden until a manual reload. Mirrors the
        // switchTenant reload (workspace-switcher.tsx). Honors ?redirect= but
        // only same-origin relative paths (open-redirect guard).
        const target = new URLSearchParams(window.location.search).get(
          'redirect',
        );
        window.location.assign(target && target.startsWith('/') ? target : '/');
      },
    },
  );

  // No /api/auth/register endpoint in M1 baseline. The register button stays
  // in the UI for layout parity but reports the gap.
  const registerService = useRequest(
    () =>
      Promise.reject(
        new Error('register endpoint lands in M2 (M1 baseline is login-only)'),
      ),
    {
      manual: true,
    },
  );

  // Belt-and-braces: if the user lands on /sign while already logged in,
  // bounce them straight back into the app.
  useEffect(() => {
    if (loginStatus === 'logined') {
      navigate('/', { replace: true });
    }
  }, [loginStatus, navigate]);

  return {
    login: loginService.run,
    register: registerService.run,
    loginLoading: loginService.loading,
    registerLoading: registerService.loading,
  };
};

export { persistToken, tokenToUserInfo, postLogin };
